import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Box, styled } from '@mui/system';
import { Grid, IconButton } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import FilterSearch from '@Components/Filter/Search';
import SbButton from '@Components/Button';

import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootStateType } from '@Store';
import Image from '@Components/Image';
import { getCarrierList } from '@Reducers/Carrier';
import { CarrierListQuery } from '@Reducers/Carrier/Type';
import { isActionAllowed } from '../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material';
import DeleteModal from '@Components/Modal/Delete';
import { deleteCarrierById } from '@Reducers/Carrier/actions';
const TableActionContainer = styled('div')(({ theme }) => ({
  padding: '32px 0px',
  display: 'grid',
  gridTemplateColumns: 'auto auto 145px',
  columnGap: '15px',
  [theme.breakpoints.down('xl')]: {
    padding: '32px 0px',
    columnGap: 0,
    gridTemplateColumns: 'auto auto 145px',
  },
}));

const SearchFilterList = [{ label: 'Name', value: 'name' }];
type DeleteCarrierProviderType = null | { id: string; name: string };

function CarrierSection() {
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const navigate = useNavigate();
  const [deleteCarrierProviderId, setDeleteCarrierProviderId] = useState<DeleteCarrierProviderType>();

  const isCreateAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Carriers.create_carriers,
    PERFORM_ACTION.write,
  );
  const dispatch = useDispatch<AppDispatch>();
  const carrier = useSelector((state: RootStateType) => state.carrier);
  const theme = useTheme();

  const handleEdit = (id: string) => {
    if (isCreateAllowed) {
      navigate(`/carrier-and-services/edit/carrier/${id}`);
    }
  };
  const handleDelete = (id: string, name: string) => {
    setDeleteCarrierProviderId({ id, name });
  };
  const onDelete = () => {
    if (deleteCarrierProviderId) {
      dispatch(deleteCarrierById(deleteCarrierProviderId.id));
      dispatch(getCarrierList({}));
    }
    setDeleteCarrierProviderId(null);
  };
  useEffect(() => {
    dispatch(getCarrierList({}));
  }, []);

  return (
    <>
      <Grid
        container
        display="grid"
        gridTemplateColumns="repeat(auto-fill, 32%)"
        justifyContent="space-between"
        rowGap={4}
      >
        {carrier?.carrierList?.map(carrier => (
          <Grid
            container
            className="shadow rounded"
            px={2}
            py={3}
            spacing={1}
            sx={{ position: 'relative' }}
            key={carrier._id}
          >
            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => {
                  handleEdit(carrier._id);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(carrier._id, carrier.name)}>
                <DeleteIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              </IconButton>
            </Box>

            <Grid
              item
              p={1}
              xs={3}
              container
              alignItems="center"
              justifyContent="center"
              className="border-1px border-light rounded"
              sx={{ width: '120px', height: '120px', overflow: 'hidden' }}
            >
              {typeof carrier.logo === 'string' && (
                <Image
                  src={carrier.logo}
                  alt={carrier?.name}
                  style={{ maxHeight: '100px', maxWidth: '100px', height: 'auto', width: 'auto' }}
                />
              )}
            </Grid>
            <Grid
              xs={9}
              p={1}
              container
              alignItems="center"
              justifyContent="flex-start"
              item
              sx={{ height: '120px', display: 'flex' }}
            >
              <p className="truncate md-text">{carrier?.name}</p>
            </Grid>
          </Grid>
        ))}
      </Grid>
      {deleteCarrierProviderId && (
        <DeleteModal
          title={`Do you want to delete ${deleteCarrierProviderId.name}`}
          open={true}
          onClose={() => setDeleteCarrierProviderId(null)}
          onConfirm={onDelete}
        />
      )}
    </>
  );
}

export default function CarrierList() {
  const dispatch = useDispatch<AppDispatch>();
  const [filterSelectValue, setFilterSelectValue] = useState('name');
  const [filterText, setFilterText] = useState('');
  const navigate = useNavigate();
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isCreateAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Carriers.create_carriers,
    PERFORM_ACTION.write,
  );
  const onCreate = () => {
    if (isCreateAllowed) {
      navigate('/carrier-and-services/create/carrier');
    }
  };

  interface FilterObj {
    currentPage?: number;
    perPage?: number;
    searchKey?: string;
    searchValue?: string;
  }

  const getFilterObj = (options: FilterObj): CarrierListQuery => {
    const filterSearchKey = filterSelectValue;
    const filterSearchValue = 'searchValue' in options ? options.searchValue : filterText;
    const filterQuery: CarrierListQuery = {
      // currentPage: options.currentPage || currentPage,
      // perPage: options.perPage || perPage,

      searchKey: filterSearchKey,
      searchValue: filterSearchValue,
    };
    if (!filterSearchValue) {
      delete filterQuery['searchKey'];
      delete filterQuery['searchValue'];
    }
    return filterQuery;
  };

  return (
    <>
      <TableActionContainer>
        <FilterSearch
          onSelectChange={(value: string) => {
            setFilterSelectValue(value);
          }}
          list={SearchFilterList}
          selectValue={filterSelectValue}
          textValue={filterText}
          onTextChange={e => {
            setFilterText(e.target.value);
          }}
          onTextSearch={() => {
            dispatch(getCarrierList(getFilterObj({})));
          }}
          onTextClear={() => {
            setFilterText('');
            dispatch(getCarrierList(getFilterObj({ searchValue: undefined })));
          }}
        />
        <div></div>

        <SbButton
          className="xs-text ml-1"
          variant="contained"
          sx={{ borderRadius: '8px !important', padding: '8px !important' }}
          startIcon={<AddCircleIcon fontSize="small" />}
          onClick={onCreate}
          disabled={!isCreateAllowed}
        >
          Create New
        </SbButton>
      </TableActionContainer>
      <CarrierSection />
    </>
  );
}
